import { useState, useEffect } from 'react';

/**
 * Custom hook for loading Green Atlas bundles.
 * @param {*} selectedPropertyIdentifier
 * @param {*} selectedDatabase
 * @param {*} bearerToken
 * @returns
 */
export const useGreenAtlasBundles = (
  selectedPropertyIdentifier,
  selectedDatabase,
  bearerToken
) => {
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [error, setError] = useState(null);

  const fetchGreenAtlasBundles = async () => {
    // Exit if no property selected.
    if (!selectedPropertyIdentifier) {
      setBundles([]);
      setLoadingBundles(false);
      return;
    }

    setLoadingBundles(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/green-atlas/bundles`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            SeasonalDatabase: selectedDatabase,
            Authorization: `Bearer ${bearerToken}`
          }
        }
      );

      if (!response.ok) {
        throw [response.status, response.statusText];
      }

      const json = await response.json();
      // Filter bundles to only include those for the selected property
      const filteredBundles = json.bundles.filter(
        (bundle) => bundle.property_identifier === selectedPropertyIdentifier
      );
      setBundles(filteredBundles);
    } catch (err) {
      if (err[0] === 404) {
        console.error('No Green Atlas bundles found.');
        setError('No Green Atlas bundles found.');
      } else {
        console.error('Error loading Green Atlas bundles:', err);
        setError('Failed to load Green Atlas bundles. Please try again later.');
      }
      setBundles([]);
    } finally {
      setLoadingBundles(false);
    }
  };

  useEffect(() => {
    if (selectedPropertyIdentifier && selectedDatabase && bearerToken) {
      fetchGreenAtlasBundles();
    }
  }, [selectedPropertyIdentifier, selectedDatabase, bearerToken]);

  const deleteBundle = async (bundleId) => {
    if (
      !selectedPropertyIdentifier ||
      bundleId === null ||
      bundleId === undefined
    ) {
      return false;
    }

    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/green-atlas/bundles/${selectedPropertyIdentifier}/${bundleId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            SeasonalDatabase: selectedDatabase,
            Authorization: `Bearer ${bearerToken}`
          }
        }
      );

      if (!response.ok) {
        throw [response.status, response.statusText];
      }

      // Refresh the bundles list after successful deletion
      await fetchGreenAtlasBundles();
      return true;
    } catch (err) {
      console.error('Error deleting Green Atlas bundle:', err);
      setError('Failed to delete the bundle. Please try again later.');
      return false;
    }
  };

  const updateBundleName = async (bundleId, newName) => {
    if (
      !selectedPropertyIdentifier ||
      bundleId === null ||
      bundleId === undefined
    ) {
      return false;
    }

    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/green-atlas/bundles/${selectedPropertyIdentifier}/${bundleId}/name`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            SeasonalDatabase: selectedDatabase,
            Authorization: `Bearer ${bearerToken}`
          },
          body: JSON.stringify({ name: newName })
        }
      );

      if (!response.ok) {
        throw [response.status, response.statusText];
      }

      // Refresh the bundles list after successful update
      await fetchGreenAtlasBundles();
      return true;
    } catch (err) {
      console.error('Error updating Green Atlas bundle name:', err);
      setError('Failed to update the bundle name. Please try again later.');
      return false;
    }
  };

  return {
    bundles,
    loadingBundles,
    error,
    deleteBundle,
    updateBundleName,
    refreshBundles: fetchGreenAtlasBundles
  };
};

